import { createAction, props } from "@ngrx/store";
import {
  ActiveWorkshopSystemResponse,
  BusinessProposalResponse,
  CalendarDayTimeSlotResponse,
  CampaignResponse,
  DealerResponse,
  EbuCarModelResponse,
  ExternalTyreUrlResponse,
  GetTechUpdateStatusResponse,
  InsuranceCompanyResponse,
  PackageResponse,
  PackageTypeEnum,
  SelectedPackagesRequest,
  VehicleSearchResponse,
  VehicleWarningLightsResponse,
  WorkshopSystemEnum
} from "src/domain/client";
import { FileParameter } from "src/domain/client";
import { AppError } from "./booking.state";
import {
  ContactInformation,
  IBookingError,
  SelectedPackages
} from "./utils/booking.modals";

// --- Step 1: About Vehicle ---
export const submitAboutVehicle = createAction(
  "[Booking Page] Submit About Vehicle",
  props<{ chassiNumber: string; odometer: number; searchedByRegno: boolean }>()
);

export const updateOdometer = createAction(
  "[Booking Page] Update Odometer",
  props<{ odometer: number }>()
);

export const updateProviderIds = createAction(
  "[Booking Page] Set Provider IDs",
  props<{ providerIds: string[] }>()
);

export const loadCarInfoSuccess = createAction(
  "[Booking API] Load Car Info Success",
  props<{ carInfo: VehicleSearchResponse; searchedByRegno: boolean }>()
);

export const loadCarInfoFailure = createAction(
  "[Booking API] Load Car Info Failure",
  props<{ error: AppError; carInfo?: VehicleSearchResponse }>()
);

export const clearCarInfo = createAction("[Booking Page] Clear Car Info");

export const checkTechUpdateStatus = createAction(
  "[Booking API] Check Tech Update Status",
  props<{ chassiNumber: string }>()
);

export const loadTechUpdateStatusSuccess = createAction(
  "[Booking API] Load Tech Update Status Success",
  props<{ status: GetTechUpdateStatusResponse }>()
);

export const loadTechUpdateStatusFailure = createAction(
  "[Booking API] Load Tech Update Status Failure",
  props<{ error: any }>()
);

// --- Step 2: Workshop System Selection ---
export const loadActiveWorkshopSystems = createAction(
  "[Workshop Selection Page] Load Active Workshop Systems"
);

export const loadActiveWorkshopSystemsSuccess = createAction(
  "[Booking API] Load Active Workshop Systems Success",
  props<{ systems: ActiveWorkshopSystemResponse[] }>()
);

export const loadActiveWorkshopSystemsFailure = createAction(
  "[Booking API] Load Active Workshop Systems Failure",
  props<{ error: AppError }>()
);

export const submitWorkshopSystems = createAction(
  "[Workshop Selection Page] Submit Workshop Systems",
  props<{ selectedSystems: WorkshopSystemEnum[] }>()
);

// --- Step 3: Find Dealer ---
export const loadDealers = createAction(
  "[Find Dealer Page] Load Dealers",
  props<{ loadAll?: boolean }>()
);

export const loadDealersSuccess = createAction(
  "[Booking API] Load Dealers Success",
  props<{ dealers: DealerResponse[] }>()
);

export const loadDealersFailure = createAction(
  "[Booking API] Load Dealers Failure",
  props<{ error: AppError }>()
);

export const selectDealer = createAction(
  "[Find Dealer Page] Select Dealer",
  props<{ dealerNumber: string; hasSearched?: boolean }>()
);

export const loadEnabledServiceTypesSuccess = createAction(
  "[Booking API] Load Enabled Service Types Success",
  props<{ types: PackageTypeEnum[] }>()
);
export const loadEnabledServiceTypesFailure = createAction(
  "[Booking API] Load Enabled Service Types Failure",
  props<{ error: AppError }>()
);

// --- Step 4: Choose Specifications ---
export const loadSpecificationsPageData = createAction(
  "[Specifications Page] Load Page Data",
  props<{ isPartialReload?: boolean }>()
);

export const loadSpecificationsPageDataSuccess = createAction(
  "[Booking API] Load Specifications Page Data Success",
  props<{
    basePackages: PackageResponse[];
    additionalPackages: PackageResponse[];
    segment: number;
    urlResponse: ExternalTyreUrlResponse;
    providers: InsuranceCompanyResponse[];
    lights: VehicleWarningLightsResponse[];
  }>()
);

export const loadAdditionalPackagesSuccess = createAction(
  "[Booking API] Load Additional Packages Success",
  props<{ packages: PackageResponse[] }>()
);

export const loadSpecificationsPageDataFailure = createAction(
  "[Specifications Page] Load Page Data Failure",
  props<{ error: AppError }>()
);

export const loadBasePackagesSuccess = createAction(
  "[Booking API] Load Base Packages Success",
  props<{ packages: PackageResponse[] }>()
);
export const loadBasePackagesFailure = createAction(
  "[Booking API] Load Base Packages Failure",
  props<{ error: AppError }>()
);

export const loadInsuranceProvidersSuccess = createAction(
  "[Booking API] Load Insurance Providers Success",
  props<{ providers: InsuranceCompanyResponse[] }>()
);

export const loadInsuranceProvidersFailure = createAction(
  "[Booking API] Load Insurance Providers Failure",
  props<{ error: AppError }>()
);

export const loadWarningLightsSuccess = createAction(
  "[Booking API] Load Warning Lights Success",
  props<{ lights: VehicleWarningLightsResponse[] }>()
);

export const loadWarningLightsFailure = createAction(
  "[Booking API] Load Warning Lights failure",
  props<{ error: AppError }>()
);

export const loadVehicleSegmentSuccess = createAction(
  "[Booking API] Load Vehicle Segment Success",
  props<{ segment: number }>()
);

export const loadVehicleSegmentFailure = createAction(
  "[Booking API] Load Vehicle Segment Failure",
  props<{ error: AppError }>()
);

export const loadExternalTyreUrlSuccess = createAction(
  "[Booking API] Load External Tyre URL Success",
  props<{ urlResponse: ExternalTyreUrlResponse }>()
);

export const loadExternalTyreUrlFailure = createAction(
  "[Booking API] Load External Tyre URL Failure",
  props<{ error: AppError }>()
);

export const updateSelectedPackages = createAction(
  "[Specifications Page] Update Selected Packages",
  props<{ selectedPackages: Partial<SelectedPackages> }>()
);

export const setRejectedIsOnlyTyre = createAction(
  "[Booking] Set Rejected Is Only Tyre",
  props<{ rejected: boolean }>()
);

export const businessProposalLoadedSuccess = createAction(
  "[Business Proposal API] Load Success",
  props<{ proposal: BusinessProposalResponse }>()
);

// --- Step 5: Extra Services ---
export const loadExtraServicesData = createAction(
  "[Extra Services Page] Load Extra Services"
);
export const loadExtraServicesDataSuccess = createAction(
  "[Booking API] Load Extra Services Success",
  props<{ packages: PackageResponse[] }>()
);
export const loadExtraServicesDataFailure = createAction(
  "[Booking API] Load Extra Services Failure",
  props<{ error: AppError }>()
);

// --- Step 6: Time Selection ---

export const loadAvailableTimes = createAction(
  "[Time Selection Page] Load Available Times"
);
export const loadAvailableTimesSuccess = createAction(
  "[Booking API] Load Available Times Success",
  props<{
    times: CalendarDayTimeSlotResponse[];
    packagesUsedForRequest: SelectedPackagesRequest[];
  }>()
);
export const loadAvailableTimesFailure = createAction(
  "[Booking API] Load Available Times Failure",
  props<{ error: AppError }>()
);
export const selectBookingDate = createAction(
  "[Time Selection Page] Select Booking Date",
  props<{ date: string | null }>()
);
export const selectBookingTime = createAction(
  "[Time Selection Page] Select Booking Time",
  props<{ time: string | null }>()
);

export const selectDropoffOption = createAction(
  "[Time Selection Page] Select Dropoff Option",
  props<{ dropOffId: string | null }>()
);

export const loadEbuCarsSuccess = createAction(
  "[Booking API] Load EBU Cars Success",
  props<{ cars: EbuCarModelResponse[] }>()
);

export const loadEbuCarsFailure = createAction(
  "[Booking API] Load EBU Cars Failure",
  props<{ error: AppError }>()
);

export const selectEbuCar = createAction(
  "[Time Selection Page] Select EBU Car",
  props<{ groupID: string | null }>()
);

// --- Step 7: Contact Information ---
export const validateCampaignCode = createAction(
  "[Contact Info Page] Validate Campaign Code",
  props<{ code: string }>()
);

export const validateCampaignCodeSuccess = createAction(
  "[Booking API] Validate Campaign Code Success",
  props<{ response: CampaignResponse | null }>()
);

export const validateCampaignCodeFailure = createAction(
  "[Booking API] Validate Campaign Code Failure",
  props<{ error: AppError }>()
);

export const clearCampaignCode = createAction(
  "[Contact Info Page] Clear Campaign Code"
);

export const contactInfoPageLoaded = createAction(
  "[Contact Info Page] Page Loaded"
);

export const updateContactInformation = createAction(
  "[Contact Info Page] Update Contact Information",
  props<{ info: Partial<ContactInformation> }>()
);

export const submitContactInformation = createAction(
  "[Contact Info Page] Submit Contact Information"
);

// --- Step 8: Complete Booking ---
export const submitBooking = createAction("[Complete Page] Submit Booking");

export const submitBookingSuccess = createAction(
  "[Booking API] Submit Booking Success",
  props<{ orderId: string }>()
);

export const submitBookingFailure = createAction(
  "[Booking API] Submit Booking Failure",
  props<{ error: IBookingError }>()
);

export const clearBookingResponse = createAction(
  "[Booking] Clear Booking Response"
);

// -- Shared actions ---

export const clearStateFromStep = createAction(
  "[Booking] Clear State From Step",
  props<{ stepNumber: number }>()
);

export const uploadFile = createAction(
  "[File] Upload File",
  props<{ file: FileParameter }>()
);
export const uploadFileSuccess = createAction(
  "[File API] Upload File Success",
  props<{ fileId: string }>()
);
export const uploadFileFailure = createAction(
  "[File API] Upload File Failure",
  props<{ error: AppError }>()
);
export const clearFile = createAction(
  "[File] Clear File",
  props<{ fileId: string }>()
);

// -- Dev Helpers ---
export const hydrateBookingState = createAction(
  "[Dev Helpers] Hydrate Booking State",
  props<{ state: Partial<import("./booking.state").BookingState> }>()
);
